import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import { changeState } from '@teammo/teammo-general-utils';
import history from 'app/routes/history';
import dollarsToCents from 'dollars-to-cents';
import _ from 'lodash';
import { parseAmount } from 'app/utils/parsers';
import { Link } from 'react-router-dom';
import { Form } from 'reactstrap';

import Button from 'app/components/general/Button';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import ModalConfirm from 'app/components/general/ModalConfirm';
import SectionGroup from 'app/components/general/SectionGroup';
import Alert from 'app/components/general/Alert';
import TeamMembersTableSelectableAmounts from 'app/components/teamMembers/TeamMembersTableSelectableAmounts';

import * as paymentsActions from 'app/ducks/payments/actions';
import * as paymentCollectionsActions from 'app/ducks/paymentCollections/actions';
import * as teamsActions from 'app/ducks/teams/actions';
import * as teamMembersActions from 'app/ducks/teamMembers/actions';

import * as usersSelectors from 'app/ducks/users/selectors';
import * as paymentsSelectors from 'app/ducks/payments/selectors';
import * as paymentCollectionsSelectors from 'app/ducks/paymentCollections/selectors';
import * as teamsSelectors from 'app/ducks/teams/selectors';
import * as teamMembersSelectors from 'app/ducks/teamMembers/selectors';
import collectCategories from 'app/utils/static/collectCategories';
import splitTypes from 'app/utils/static/splitTypes';
import validateInput from './CollectPaymentValidator';

function selector(state): any {
  return {
    payments: paymentsSelectors.getPayments(state),
    paymentCollections: paymentCollectionsSelectors.getPaymentCollections(
      state,
    ),
    teams: teamsSelectors.getTeams(state),
    users: usersSelectors.getUsers(state),
    teamMembers: teamMembersSelectors.getTeamMembers(state),
    isLoading: state.payments.isLoading,
    currentCollectPayment: paymentsSelectors.getCurrentFormPayment(state),
    errors: { ...state.payments.backendErrors },
  };
}

const actionCreators = _.merge(
  paymentsActions,
  paymentCollectionsActions,
  teamsActions,
  teamMembersActions,
);

interface ITeamMemberRow {
  [id: string]: {
    selected?: boolean;
    amount?: string;
    id?: string;
    floatAmount?: number;
  };
}

interface ICollectPaymentProps {
  actions: any;
  payments: any;
  teamMembers: any;
  users: any;
  isLoading: boolean;
  teams: any;
  errors: any;
}

interface ICollectPaymentState {
  selectedTeam?: any;
  collectCategory?: any;
  splitType?: any;
  description?: string;
  amount?: string;
  floatAmount?: string;
  dueAt?: string;
  status?: string;
  changed?: boolean;
  changedSelectedRow?: boolean;
  selectedAllRows?: boolean;
  confirmModalOpen?: boolean;
  teamMemberRows?: ITeamMemberRow[];
  errors?: any;
}

const defaultRows = (teamMemberIds): any => {
  const rows = {};
  _.map(teamMemberIds, (id) => {
    rows[id] = {
      id,
      selected: false,
      amount: undefined,
      customAmount: false,
    };
  });
  return rows;
};

@(connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
})) as any)
export default class CollectPaymentTeam extends React.Component<
ICollectPaymentProps,
ICollectPaymentState
> {
  static defaultProps: ICollectPaymentProps = {
    actions: undefined,
    payments: undefined,
    users: undefined,
    teamMembers: undefined,
    isLoading: false,
    teams: undefined,
    errors: {},
  };

  state: ICollectPaymentState = {
    selectedTeam: {
      id: this.props.teams.currentTeam?.id,
      name: this.props.teams.currentTeam?.name,
    },
    collectCategory: collectCategories[0],
    description: '',
    splitType: splitTypes[0],
    amount: '',
    floatAmount: '',
    dueAt: '',
    status: '',
    teamMemberRows: {},
    changed: false,
    changedSelectedRow: false,
    selectedAllRows: false,
    confirmModalOpen: false,
    errors: {},
  };

  componentDidMount(): void {
    this.props.actions.getAllTeams();
    this.props.actions.getAllTeamMembers(
      {
        filter: { 'team-id': this.props.teams.currentTeamId },
        page: {
          size: 200,
        },
      },
      false,
    );
    this.props.actions.getAllPayments();
  }

  componentDidUpdate(prevProps): void {
    if (
      prevProps.teams.currentTeamId !== this.props.teams.currentTeamId
      && !this.props.teamMembers.isLoading
    ) {
      this.props.actions.getAllTeamMembers(
        {
          filter: { 'team-id': this.props.teams.currentTeamId },
          page: {
            size: 200,
          },
        },
        false,
      );
    }
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      return {
        ...prevState,
        description: nextProps.currentCollectPayment.description || '',
        errors: nextProps.errors,
        teamMemberRows: defaultRows(nextProps.teamMembers.teamMemberIds),
      };
    }
    return null;
  }

  handleCancel = (e): void => {
    e.preventDefault();
    history.push('/dashboard');
  };

  handleChange = (e): void => {
    e.persist();
    this.setState((previousState) => ({
      ...changeState(previousState, e.target),
      changed: true,
      errors: {},
    }));
  };

  handleSelectTeam = (e): void => {
    this.props.actions.getAllTeamMembers(
      {
        filter: { 'team-id': e.id },
        page: {
          size: 200,
        },
      },
      false,
    );
    this.setState({ selectedTeam: e });
  };

  handleSelectCollectCategory = (e): void => {
    this.setState({ collectCategory: e });
  };

  handleSelectSplitType = (e): void => {
    this.setState((previousState) => ({ splitType: e, teamMemberRows: this.calculateRows(previousState, e.id, previousState.floatAmount, this.selectedTeamMemberIds(previousState.teamMemberRows)) }));
  };

  handleSubmit = (e): void => {
    e.preventDefault();

    if (
      this.isValid()
      && !this.props.payments.isLoading
      && this.props.teamMembers.hasLoaded
    ) {
      this.setState({ changed: false, confirmModalOpen: false });
      const teamMembers = _.map(this.selectedTeamMembers(this.state.teamMemberRows), (o) => ({
        id: o.id,
        amount: dollarsToCents((o.floatAmount) || (this.amountCalculated() / 100)),
      }));
      const consumerIds = _.map(this.selectedTeamMemberIds(this.state.teamMemberRows), (o) => ({
        id: this.props.teamMembers.teamMembersById[o].consumer.id,
      }));
      const payload = {
        description: this.state.description,
        amount: dollarsToCents(this.amountCalculated() / 100),
        paymentCategory: 'team-collection',
        paymentType: 'team-manager',
        team: {
          id: this.props.teams.currentTeam.id,
        },
        teamMembers,
        consumers: consumerIds,
      };
      this.props.actions.createPayment(payload);
    } else {
      this.setState({ confirmModalOpen: false });
    }
  };

  handleAmountChange = (values): void => {
    const { formattedValue, value } = values;
    this.setState((previousState) => {
      const selectedAllRows = !previousState.changed;
      const selectedRows = previousState.changedSelectedRow ? previousState.teamMemberRows : this.selectAllRows(previousState.teamMemberRows, !_.isEmpty(value));
      const allRowsSelected = this.allRowsSelected(this.props.teamMembers.teamMemberIds, this.selectedTeamMemberIds(selectedRows));
      return {
        amount: formattedValue,
        floatAmount: value,
        teamMemberRows: this.calculateRows(previousState, previousState.splitType.id, value, this.selectedTeamMemberIds(selectedRows)),
        selectedAllRows: !_.isEmpty(value) && (selectedAllRows || allRowsSelected),
        changed: true,
        errors: {},
      };
    });
  };

  handleRowAmountChange = (id, values): void => {
    const { formattedValue, value } = values;
    this.setState((previousState) => {
      const customAmount = previousState.floatAmount !== value;
      return {
        changed: true,
        teamMemberRows: {
          ...previousState.teamMemberRows,
          [id]: {
            ...previousState.teamMemberRows[id],
            id,
            amount: formattedValue,
            customAmount,
            floatAmount: value,
          },
        },
      };
    });
  };

  handleSelectAllRows = (e): void => {
    this.setState((previousState) => {
      const selectionValue = !this.state.selectedAllRows;
      const selectedRows = this.selectAllRows(previousState.teamMemberRows, selectionValue);
      const teamMemberRows = this.calculateRows(previousState, previousState.splitType.id, previousState.floatAmount, this.selectedTeamMemberIds(selectedRows));
      return {
        changed: true,
        changedSelectedRow: false,
        selectedAllRows: !previousState.selectedAllRows,
        teamMemberRows,
      };
    });
  }

  handleOpenModal = (e): void => {
    e.preventDefault();
    const hasMemberSelected = this.numberOfSelectedTeamMembers() > 0;
    if (!this.state.description) {
      this.setState({
        changed: true,
        errors: {
          description: 'cannot be blank',
        },
      });
    } else if (!(!this.state.description
      || !this.state.amount
      || !hasMemberSelected
      || !this.props.teamMembers.hasLoaded)) {
      this.setState({ confirmModalOpen: true });
    }
  };

  handeCloseModal = (e): void => {
    e.preventDefault();
    this.setState({ confirmModalOpen: false });
  };

  selectRow = (id): void => {
    this.setState((previousState) => {
      const newState = {
        ...previousState,
        teamMemberRows: {
          ...previousState.teamMemberRows,
          [id]: {
            ...previousState.teamMemberRows[id],
            selected: previousState.teamMemberRows[id]?.selected === undefined ? true : !previousState.teamMemberRows[id]?.selected,
          },
        },
      };
      const selectedAllRows = this.allRowsSelected(this.props.teamMembers.teamMemberIds, this.selectedTeamMemberIds(newState.teamMemberRows));
      return {
        changed: true,
        changedSelectedRow: true,
        teamMemberRows: this.calculateRows(previousState, newState.splitType.id, newState.floatAmount, this.selectedTeamMemberIds(newState.teamMemberRows)),
        selectedAllRows,
      };
    });
  };

  selectAllRows = (teamMemberRows, selectionValue = !this.state.selectedAllRows): any => {
    const selectedRows = {};

    _.map(teamMemberRows, (value, key) => {
      selectedRows[key] = {
        selected: selectionValue,
      };
    });

    return selectedRows;
  };

  perPersonRows = (previousState, amount, selectedTeamMemberIds): any => {
    const rows = {};

    _.map(this.props.teamMembers.teamMemberIds, (id) => {
      const selected = _.includes(selectedTeamMemberIds, id);
      const parsedAmount = previousState.teamMemberRows[id].customAmount ? previousState.teamMemberRows[id].amount : amount;
      rows[id] = { ...previousState.teamMemberRows[id], amount: selected ? parsedAmount : '', selected };
    });
    return rows;
  }

  totalRows = (previousState, amount, selectedTeamMemberIds): any => {
    const rows = {};

    const numberOfTeamMembers = selectedTeamMemberIds.length;
    const splitAmount = (parseFloat(amount) / numberOfTeamMembers) * 100;
    _.map(this.props.teamMembers.teamMemberIds, (id) => {
      const selected = _.includes(selectedTeamMemberIds, id);

      rows[id] = { ...previousState.teamMemberRows[id], amount: selected ? parseAmount(splitAmount) : '', selected };
    });

    return rows;
  }

  calculateRows = (previousState, splitType, amount, selectedTeamMemberIds): any => {
    let rows = {};
    switch (splitType) {
      case 'per-person':
        rows = this.perPersonRows(previousState, amount, selectedTeamMemberIds);
        break;
      case 'total':
        rows = this.totalRows(previousState, amount, selectedTeamMemberIds);
        break;
      default:
        return {};
    }
    const filteredRows = _.pickBy(rows, (value, key) => !_.isEmpty(key));
    return filteredRows;
  }

  allRowsSelected = (teamMembersById, selectedRows): boolean => teamMembersById.length === selectedRows.length

  selectedTeamMemberIds = (teamMemberRows): string[] => _.compact(_.map(teamMemberRows, (row, key) => {
    if (row.selected) {
      return key;
    }
  }))

  selectedTeamMembers = (teamMemberRows: ITeamMemberRow[]): ITeamMemberRow[] => _.compact(_.map(teamMemberRows, (row) => {
    if (row.selected) {
      return row;
    }
  }))

  numberOfSelectedTeamMembers = (teamMemberRows = this.state.teamMemberRows): number => this.selectedTeamMemberIds(teamMemberRows).length

  amountDescription = (): string => {
    switch (this.state.splitType.id) {
      case 'per-person':
        return '($ per person)';
      case 'total':
        return '($ split between)';
      default:
        return '';
    }
  }

  amountCalculated = (): number => {
    let amount = parseFloat(this.state.floatAmount) * 100;
    switch (this.state.splitType.id) {
      case 'total':
        amount = (parseFloat(this.state.floatAmount) / this.numberOfSelectedTeamMembers()) * 100;
        break;
      default:
        break;
    }
    return amount;
  }

  isValid(): boolean {
    const { errors, isValid } = validateInput(this.state);

    if (!isValid) {
      this.setState({ errors });
    }

    return isValid;
  }

  render(): React.ReactNode {
    const hasMemberSelected = this.numberOfSelectedTeamMembers() > 0;
    return (
      <Form onSubmit={this.handleOpenModal}>
        <div className="row">
          <div className="col col-lg-10 col-xl-8">
            <div className="row">
              <div className="col">
                <div className="form-description">
                  Collect money from team members direct to your bank account.
                  They will each receive an SMS from Teammo where they can pay
                  the requested amount via credit/debit card.
                  {' '}
                  <a href="https://help.teammo.com.au/en/articles/1379372-how-do-i-request-a-payment-from-a-team-member" target="_blank">Learn more</a>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <div className="form-description">
                  Once a team member has paid, you can expect to receive those funds in your nominated bank account within
                  {' '}
                  <strong>1-3 business days</strong>
                  .
                  {' '}
                  <a href="https://help.teammo.com.au/en/articles/3530012-how-long-does-it-take-to-receive-payments-from-team-members" target="_blank">Read more</a>
                </div>
              </div>
            </div>
            {this.props.payments.paymentIds.length === 0 && (
              <div className="row">
                <div className="col">
                  <div className="form-description">
                    Your first payment request via Teammo may take an extra business day due to standard security checks conducted on all accounts.
                  </div>
                </div>
              </div>
            )}
            {_.isEmpty(this.props.users.currentUser.bankAccount.id) && (
              <div className="row with-margin-bottom-10 with-margin-top-10">
                <div className="col">

                  <Alert color="warning" className="with-margin-bottom-10">
                    <div className="d-inline-block">
                      <h4>
                        No bank account attached to your Teammo account
                      </h4>
                      <span className="with-margin-top">
                        You may request funds from team members, but you will not
                        receive funds.
                        {' '}
                        <Link to="/profile/bank">Add a bank account</Link>
                      </span>
                    </div>
                  </Alert>
                </div>
              </div>
            )}
            <div className="row with-margin-top">
              <div className="col">
                <SectionGroup title="Payment Details">
                  <div className="row">
                    <div className="col col-12">
                      <InputGroupBlock
                        type="select"
                        name="type"
                        labelText="Team"
                        placeholder="Team"
                        tabIndex="1"
                        errors={this.state.errors}
                        value={this.state.selectedTeam}
                        onChange={this.handleSelectTeam}
                        options={this.props.teams.teamsByIdInSelectFormat}
                      />
                    </div>
                    <div className="col col-12">
                      <InputGroupBlock
                        type="select"
                        name="type"
                        labelText="Category"
                        placeholder="Category"
                        tabIndex="1"
                        errors={this.state.errors}
                        value={this.state.collectCategory}
                        onChange={this.handleSelectCollectCategory}
                        options={collectCategories}
                      />
                    </div>
                    <div className="col col-12">
                      <InputGroupBlock
                        name="description"
                        labelText="Description"
                        placeholder="Match payment"
                        tabIndex="2"
                        errors={this.state.errors}
                        value={this.state.description}
                        onChange={this.handleChange}
                      />
                    </div>
                    <div className="col col-6 col-sm-4">
                      <InputGroupBlock
                        type="select"
                        name="type"
                        labelText="Type"
                        placeholder="Type"
                        tabIndex="3"
                        errors={this.state.errors}
                        value={this.state.splitType}
                        onChange={this.handleSelectSplitType}
                        options={splitTypes}
                      />
                    </div>
                    <div className="col col-6 col-sm-8">
                      <InputGroupBlock
                        name="amount"
                        labelText={`Amount ${this.amountDescription()}`}
                        placeholder="$0.00"
                        type="currency"
                        tabIndex="4"
                        errors={this.state.errors}
                        value={this.state.amount}
                        onChange={this.handleAmountChange}
                      />
                    </div>
                  </div>
                  <div className="row with-margin-top">
                    <div className="col col-12">
                      <TeamMembersTableSelectable
                        {...this.props.teamMembers}
                        headerTitle="Select Team Members"
                        teamMembersById={this.props.teamMembers.teamMembersById}
                        teamMemberIds={this.props.teamMembers.teamMemberIds}
                        // updateSelectedRows={this.updateSelectTeamMembers}
                        handleAmountChange={this.handleRowAmountChange}
                        rows={this.state.teamMemberRows}
                        actions={this.props.actions}
                        selectRow={this.selectRow}
                        selectAllRows={this.handleSelectAllRows}
                        selectedAllRows={this.state.selectedAllRows}
                        defaultAmount={this.state.amount}
                        splitType={this.state.splitType.id}
                        persistentFilterConfig={{
                          teamId: this.props.teams.currentTeamId,
                        }}
                        selectTeamMember={this.props.actions.selectTeamMember}
                      />
                    </div>
                  </div>
                  <div className="row with-margin-top">
                    <div className="col">
                      <div className="form-description">
                        Please note - A 1.9% + $0.20 service fee is charged to each team
                        member, on top of the amount you request.
                      </div>
                    </div>
                  </div>
                </SectionGroup>
              </div>
            </div>
          </div>
        </div>
        <div className="row with-margin-top">
          <div className="col btn-flex-group">
            <div className="btn-flex-container">
              <Button
                className={classnames('btn-1', {
                  disabled: !this.state.description
                    || !this.state.amount
                    || !hasMemberSelected
                    || !this.props.teamMembers.hasLoaded,
                })}
                type="submit"
                isLoading={this.props.payments.isSaving}
                hasSaved={this.props.payments.hasSaved}
                hasFailed={this.props.payments.hasFailed}
              >
                Request Payment
              </Button>

              <ModalConfirm
                title="Confirm Payment Request"
                description={`Are you sure you want to request money from ${this.numberOfSelectedTeamMembers()} team members?`}
                buttonText="Send Request"
                buttonClassName="btn-1 primary"
                isOpen={this.state.confirmModalOpen}
                isLoading={this.props.isLoading}
                handleSubmit={this.handleSubmit}
                handleCancel={this.handeCloseModal}
              />
            </div>
          </div>
        </div>
      </Form>
    );
  }
}
